import { useToast } from '@cad-arc';
import { useEffect, useMemo, useRef } from 'react';
import { isEmpty, throttle } from 'lodash';
import { ConversationMessageView } from '@mark43/cad-api';
import { VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import { useMessagingStore } from './use-messaging-store';
import { useMarkConversationAsRead } from './resource/use-mark-conversation-as-read';
import {
    CLEAR_UNREAD_MESSAGES_LINE_DELAY_MILLIS,
    CHECK_FOR_READ_CONVERSATION_THROTTLE_MILLIS,
} from '../constants';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';
import { useWorkspacePanel } from '../../panels/hooks/use-workspace-panel';
import { useApplicationType } from '../../common/hooks/use-application-type';

export const useMarkVisibleAsRead = (
    messageRowVirtualizer: Virtualizer<HTMLDivElement, Element>,
    virtualMessageRows: VirtualItem[],
    oldestUnreadMessageId?: number,
    newestMessageId?: number
) => {
    const toast = useToast();
    const { selectedConversationKey, selectedConversationMessages } = useMessagingStore();

    const { isFirstResponder } = useApplicationType();

    const markConversationAsRead = useMarkConversationAsRead({
        conversationKey: selectedConversationKey,
        onError: () => {
            toast({
                id: `mark-conversation-read-${selectedConversationKey?.toString()}`,
                status: 'error',
                description: errorOccurredWhile('marking a conversation as read'),
            });
        },
        oldestUnreadMessageId,
        newestMessageId,
    });

    // The goal of the throttled functions below are:
    //
    // * useEffect triggers when the currently visible messages change (virtualMessageRows) and
    //   triggers throttledmarkConversationAsRead
    //
    // * throttledmarkConversationAsRead: checks to see if the user is viewing the unread line, throttled to making
    //   one call per 500 ms to avoid iterating through virtualMessageRows too often
    //
    // * throttledReadMessages: call markAsRead with a 2.5 second throttle on leading edge. This means that this function
    //   will only trigger once every two seconds. A timeout is also used to cause the unread line to be removed 2.5 seconds
    //   after this function was called.

    const markAsReadTimeoutIdRef = useRef<NodeJS.Timeout>();

    const throttledMarkConversationAsRead = useMemo(
        () =>
            throttle(
                () => {
                    markAsReadTimeoutIdRef.current = setTimeout(() => {
                        markAsReadTimeoutIdRef.current = undefined;
                        markConversationAsRead();
                    }, CLEAR_UNREAD_MESSAGES_LINE_DELAY_MILLIS);
                },
                CLEAR_UNREAD_MESSAGES_LINE_DELAY_MILLIS,
                { leading: true }
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [oldestUnreadMessageId]
    );

    const panel = useWorkspacePanel(isFirstResponder);

    const throttledReadMessages = useMemo(() => {
        return throttle(
            (
                virtualMessageRows: VirtualItem[],
                conversationMessages: ConversationMessageView[],
                oldestUnreadMessageId: number
            ) => {
                const isViewingOldestUnreadMessage = virtualMessageRows.some(
                    (virtualMessageRow) => {
                        const message = conversationMessages[virtualMessageRow.index];
                        return (
                            message !== undefined &&
                            message.id === oldestUnreadMessageId &&
                            messageRowVirtualizer.scrollOffset <= virtualMessageRow.start
                        );
                    }
                );
                if (isViewingOldestUnreadMessage) {
                    if (isFirstResponder || (!isEmpty(panel) && panel.getIsFocused())) {
                        throttledMarkConversationAsRead();
                    }
                }
            },
            CHECK_FOR_READ_CONVERSATION_THROTTLE_MILLIS,
            // leading set ti true to match the throttled update unread message button behavior
            { leading: true }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [throttledMarkConversationAsRead]);

    useEffect(() => {
        if (
            oldestUnreadMessageId !== undefined &&
            selectedConversationKey !== undefined &&
            selectedConversationMessages !== undefined
        ) {
            throttledReadMessages(
                virtualMessageRows,
                selectedConversationMessages,
                oldestUnreadMessageId
            );
        }
    }, [
        virtualMessageRows,
        selectedConversationMessages,
        selectedConversationKey,
        oldestUnreadMessageId,
        throttledReadMessages,
        throttledMarkConversationAsRead,
        messageRowVirtualizer.scrollOffset,
    ]);

    useEffect(() => {
        return () => {
            // cancel the delayed mark conversation as read processing when oldestUnreadMessageId changes (triggered by
            // different conversation selected or conversation marked as read) since the conversation ids ingested by
            // the mutation will change when switching conversations.
            const timeoutId = markAsReadTimeoutIdRef.current;

            throttledReadMessages.cancel();
            throttledMarkConversationAsRead.cancel();

            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
                markConversationAsRead();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [oldestUnreadMessageId]);
};
